
export type PerkId =
  | 'damage_up'
  | 'hp_up'
  | 'speed_up'
  | 'fire_rate_up'
  | 'crit_chance'
  | 'regen'
  | 'piercing'
  | 'ricochet'
  | 'magnetic'
  | 'vampiric'
  | 'explosive_kills'
  | 'shield_cycle'
  // Phase 2q
  | 'multishot_forward'
  | 'side_shots'
  | 'diagonal_shots'
  | 'rear_shot'
  | 'bouncy_walls'
  | 'big_bullet'
  | 'skewer'
  | 'orbit_blade'
  // Phase 2r
  | 'ignite'
  | 'toxin'
  | 'chain_lightning'
  | 'cryo';

export type PerkRarity = 'common' | 'rare' | 'epic' | 'legendary';

export const RARITY_WEIGHTS: Record<PerkRarity, number> = {
  common: 50,
  rare: 30,
  epic: 15,
  legendary: 5,
};

export type Perk = {
  id: PerkId;
  name: string;
  description: string;
  rarity: PerkRarity;
};

export const PERK_POOL: readonly Perk[] = [
  // Common — flat stat bumps (5)
  { id: 'damage_up', name: 'Plasma Edge', description: '+10% damage', rarity: 'common' },
  { id: 'hp_up', name: 'Bio-Plating', description: '+25 max HP, heal to full', rarity: 'common' },
  { id: 'speed_up', name: 'Reflex Booster', description: '+15% move speed', rarity: 'common' },
  { id: 'fire_rate_up', name: 'Trigger Discipline', description: '+20% fire rate', rarity: 'common' },
  { id: 'regen', name: 'Nano-Healers', description: 'Regen 1 HP/sec', rarity: 'common' },
  // Rare (4)
  { id: 'crit_chance', name: 'Predator Optics', description: '+15% crit chance', rarity: 'rare' },
  { id: 'piercing', name: 'Rail Spike', description: 'Bullets pierce 1 enemy', rarity: 'rare' },
  { id: 'ricochet', name: 'Bounce Casing', description: 'Bullets ricochet once', rarity: 'rare' },
  { id: 'vampiric', name: 'Vampiric Edge', description: '+2 HP per kill', rarity: 'rare' },
  // Epic (11)
  { id: 'magnetic',          name: 'Magnet Field',   description: 'Loot pickup radius ×3',                       rarity: 'epic' },
  { id: 'explosive_kills',   name: 'Kinetic Bloom',  description: 'Kills explode (15 dmg, 60px)',                rarity: 'epic' },
  { id: 'shield_cycle',      name: 'Aegis Cycler',   description: '1 i-frame per 8s',                            rarity: 'epic' },
  { id: 'multishot_forward', name: 'Plasma Volley',  description: '+1 extra forward bullet (×0.85 dmg)',         rarity: 'epic' },
  { id: 'side_shots',        name: 'Lateral Pulse',  description: '+2 side bullets at ±90° (×0.70 dmg)',         rarity: 'epic' },
  { id: 'diagonal_shots',    name: 'Cross Spread',   description: '+2 diagonal bullets at ±45° (×0.75 dmg)',     rarity: 'epic' },
  { id: 'rear_shot',         name: 'Trail Strike',   description: '+1 rear bullet at 180° (×0.70 dmg)',          rarity: 'epic' },
  { id: 'big_bullet',        name: 'Heavy Slug',     description: 'Bullet ×1.5 size, ×1.3 dmg, ×0.8 fire rate',  rarity: 'epic' },
  { id: 'skewer',            name: 'Skewer',         description: '+2 pierce (or +1 if no Piercing yet)',        rarity: 'epic' },
  { id: 'ignite',            name: 'Pyro Rounds',    description: 'Hits set enemies on fire (4 dmg/s, 3s)',      rarity: 'epic' },
  { id: 'toxin',             name: 'Venom Coating',  description: 'Hits poison + slow (3 dmg/s, 4s, −30% speed)',rarity: 'epic' },
  // Legendary (4)
  { id: 'bouncy_walls',    name: 'Wallride',       description: 'Bullets bounce off arena walls once',         rarity: 'legendary' },
  { id: 'orbit_blade',     name: 'Solar Cutter',   description: 'Spawns an orbital blade around the cat',      rarity: 'legendary' },
  { id: 'chain_lightning', name: 'Arc Discharge',  description: '50% chance to chain to 2 nearby (60% dmg)',   rarity: 'legendary' },
  { id: 'cryo',            name: 'Cryo Rounds',    description: '25% chance to freeze for 1.5s',               rarity: 'legendary' },
];

export type Stats = {
  maxHp: number;
  hp: number;
  damageMultiplier: number;
  moveSpeed: number;
  fireRate: number;
  critChance?: number;
  regenPerSec?: number;
  pierce?: number;
  ricochet?: number;
  // Phase 2c:
  lootPickupRadius?: number;
  lifeStealPerKill?: number;
  explosionRadius?: number;
  explosionDamage?: number;
  shieldCycleSec?: number;
  // Phase 2q: projectile transformers
  extraForwardShots?: number;  // count of extra parallel forward bullets
  sideShots?: boolean;          // emit ±90° bullets
  diagonalShots?: boolean;      // emit ±45° bullets
  rearShot?: boolean;           // emit 180° bullet
  bouncyWalls?: number;         // bounces allowed per bullet off world bounds
  bulletScale?: number;         // visual + hitbox scale multiplier (default 1)
  bulletDamageMult?: number;    // multiplier applied at emission (default 1)
  orbitBlades?: number;         // count of orbital blade entities
  // Phase 2r: on-hit elemental effects
  burnOnHit?: boolean;
  poisonOnHit?: boolean;
  chainOnHit?: boolean;
  freezeOnHit?: boolean;
  // Phase 2t: synergy/evolution flags
  chainAlsoFreezes?: boolean;   // tesla_coil
  burnKillsExplode?: boolean;   // phoenix_strike
  bladeDamageMult?: number;     // heavy_orbit (default 1)
};

/**
 * Phase 2s: weighted sample-without-replacement. Each perk's weight is
 * RARITY_WEIGHTS[perk.rarity] (common 50 / rare 30 / epic 15 / legendary 5).
 * Same signature as before so call sites in runStore are unaffected.
 *
 * Algorithm: for each slot, compute total weight of remaining eligible perks,
 * pick rng * total, walk the cumulative weight to find the slot, remove that
 * perk from the eligible pool, repeat.
 */
export function choosePerkOffers(input: {
  rng: () => number;
  excludeIds: ReadonlySet<PerkId>;
  count?: number;
}): Perk[] {
  const requested = input.count ?? 3;
  const eligible: Perk[] = PERK_POOL.filter((p) => !input.excludeIds.has(p.id));
  const cap = Math.min(requested, eligible.length);

  const picks: Perk[] = [];
  for (let i = 0; i < cap; i++) {
    const totalWeight = eligible.reduce((sum, p) => sum + RARITY_WEIGHTS[p.rarity], 0);
    if (totalWeight <= 0) break;
    const target = input.rng() * totalWeight;
    let cumulative = 0;
    let chosenIdx = eligible.length - 1; // fallback to last (handles floating-point edge)
    for (let j = 0; j < eligible.length; j++) {
      cumulative += RARITY_WEIGHTS[eligible[j]!.rarity];
      if (target < cumulative) {
        chosenIdx = j;
        break;
      }
    }
    picks.push(eligible[chosenIdx]!);
    eligible.splice(chosenIdx, 1);
  }
  return picks;
}

export function applyPerk(stats: Stats, id: PerkId): Stats {
  switch (id) {
    case 'damage_up':
      return { ...stats, damageMultiplier: stats.damageMultiplier + 0.1 };
    case 'hp_up': {
      const maxHp = stats.maxHp + 25;
      return { ...stats, maxHp, hp: maxHp };
    }
    case 'speed_up':
      return { ...stats, moveSpeed: stats.moveSpeed * 1.15 };
    case 'fire_rate_up':
      return { ...stats, fireRate: stats.fireRate * 1.2 };
    case 'crit_chance':
      return { ...stats, critChance: (stats.critChance ?? 0) + 0.15 };
    case 'regen':
      return { ...stats, regenPerSec: (stats.regenPerSec ?? 0) + 1 };
    case 'piercing':
      return { ...stats, pierce: (stats.pierce ?? 0) + 1 };
    case 'ricochet':
      return { ...stats, ricochet: (stats.ricochet ?? 0) + 1 };
    case 'magnetic':
      return { ...stats, lootPickupRadius: (stats.lootPickupRadius ?? 60) * 3 };
    case 'vampiric':
      return { ...stats, lifeStealPerKill: (stats.lifeStealPerKill ?? 0) + 2 };
    case 'explosive_kills':
      return {
        ...stats,
        explosionRadius: Math.max(stats.explosionRadius ?? 0, 60),
        explosionDamage: (stats.explosionDamage ?? 0) + 15,
      };
    case 'shield_cycle':
      return {
        ...stats,
        shieldCycleSec: stats.shieldCycleSec ? Math.max(1, stats.shieldCycleSec / 2) : 8,
      };
    case 'multishot_forward':
      return { ...stats, extraForwardShots: (stats.extraForwardShots ?? 0) + 1 };
    case 'side_shots':
      return { ...stats, sideShots: true };
    case 'diagonal_shots':
      return { ...stats, diagonalShots: true };
    case 'rear_shot':
      return { ...stats, rearShot: true };
    case 'bouncy_walls':
      return { ...stats, bouncyWalls: (stats.bouncyWalls ?? 0) + 1 };
    case 'big_bullet':
      return {
        ...stats,
        bulletScale: (stats.bulletScale ?? 1) * 1.5,
        bulletDamageMult: (stats.bulletDamageMult ?? 1) * 1.3,
        fireRate: stats.fireRate * 0.8,
      };
    case 'skewer':
      return {
        ...stats,
        pierce: (stats.pierce ?? 0) > 0 ? (stats.pierce ?? 0) + 2 : 1,
      };
    case 'orbit_blade':
      return { ...stats, orbitBlades: (stats.orbitBlades ?? 0) + 1 };
    case 'ignite':
      return { ...stats, burnOnHit: true };
    case 'toxin':
      return { ...stats, poisonOnHit: true };
    case 'chain_lightning':
      return { ...stats, chainOnHit: true };
    case 'cryo':
      return { ...stats, freezeOnHit: true };
  }
}
